// src/services/database.js
// Database abstraction layer with provider switching support
// Supports: Supabase (default), Google Sheets, Local (IndexedDB) and Firebase (Firestore)
//
// To switch providers, set the environment variable:
//   REACT_APP_DB_PROVIDER=supabase (default)
//   REACT_APP_DB_PROVIDER=sheets (for Google Sheets)
//   REACT_APP_DB_PROVIDER=local (for local IndexedDB, no server needed)
//   REACT_APP_DB_PROVIDER=firebase (for Cloud Firestore + Firebase Auth)

import { supabase } from '../lib/supabase'
import { normalizeRecord, prepareRecord, REFERENCE_FIELDS, V2_TABLES } from '../lib/entitySchema'

// Determine which database provider to use
const DB_PROVIDER = process.env.REACT_APP_DB_PROVIDER || 'supabase'
const USE_GOOGLE_SHEETS = DB_PROVIDER === 'sheets'
const USE_LOCAL = DB_PROVIDER === 'local'
const USE_FIREBASE = DB_PROVIDER === 'firebase'

console.log(`🗄️ Database Provider: ${DB_PROVIDER.toUpperCase()}`)
console.log(`📊 USE_GOOGLE_SHEETS: ${USE_GOOGLE_SHEETS}`)
console.log(`💾 USE_LOCAL (IndexedDB): ${USE_LOCAL}`)
console.log(`🔥 USE_FIREBASE (Firestore): ${USE_FIREBASE}`)

// ============================================================================
// PROVIDER-AWARE SERVICE WRAPPER
// Wraps each service method to use the appropriate provider
// ============================================================================

// Lazy load Google Sheets services if needed
let sheetsServices = null
const getSheetsServices = async () => {
  if (!sheetsServices) {
    sheetsServices = await import('./googleSheetsDatabase')
  }
  return sheetsServices
}

// Lazy load Local (IndexedDB) services if needed
let localServices = null
const getLocalServices = async () => {
  if (!localServices) {
    localServices = await import('./localDatabase')
  }
  return localServices
}

// Lazy load Firestore services if needed
let firestoreServices = null
const getFirestoreServices = async () => {
  if (!firestoreServices) {
    firestoreServices = await import('./firestoreDatabase')
  }
  return firestoreServices
}

// Create a proxy that delegates to Supabase, Google Sheets, or Local IndexedDB
export const currentUser = async () => {
  if (USE_LOCAL) {
    const user = JSON.parse(localStorage.getItem('spcc:auth:user') || 'null')
    if (!user?.id) throw new Error('Sign in to continue.')
    return user
  }
  if (USE_FIREBASE) {
    const { getFirebaseUser, toAppUser } = await import('../lib/firebase')
    const user = toAppUser(await getFirebaseUser())
    if (!user) throw new Error('Sign in to continue.')
    return user
  }
  const { data, error } = await supabase.auth.getUser()
  if (error || !data?.user) throw new Error('Sign in to continue.')
  return data.user
}

// Every write announces 'spcc:data-changed', which makes the Product Brain reload
// all collections. Bulk writes run inside batchDataChanges so listeners hear one
// event at the end instead of one per record.
let batchDepth = 0
let changedInBatch = false
const notifyDataChanged = () => {
  if (batchDepth > 0) changedInBatch = true
  else window.dispatchEvent(new Event('spcc:data-changed'))
}
export async function batchDataChanges(fn) {
  batchDepth++
  try {
    return await fn()
  } finally {
    if (--batchDepth === 0 && changedInBatch) {
      changedInBatch = false
      window.dispatchEvent(new Event('spcc:data-changed'))
    }
  }
}

const createProviderProxy = (supabaseService, sheetsServiceName, localServiceName) => {
  const table = supabaseService.tableName
  let proxy
  proxy = new Proxy(supabaseService, {
    get(target, prop) {
      if (typeof target[prop] !== 'function') return target[prop]
      return async (...args) => {
        const user = await currentUser()
        let service = target
        if (USE_LOCAL) {
          const local = await getLocalServices()
          service = local[localServiceName] || new local.LocalDatabaseService(table)
        } else if (USE_FIREBASE) {
          const firestore = await getFirestoreServices()
          service = firestore[localServiceName] || new firestore.FirestoreDatabaseService(table)
        } else if (USE_GOOGLE_SHEETS) {
          const sheets = await getSheetsServices()
          service = sheets[sheetsServiceName] || new sheets.GoogleSheetsDatabaseService(table)
        }
        // All evidence writes use the same canonical mapping, including updates.
        if (table === 'evidence') {
          service = USE_LOCAL ? new (await getLocalServices()).LocalDatabaseService(table)
            : USE_FIREBASE ? new (await getFirestoreServices()).FirestoreDatabaseService(table)
            : USE_GOOGLE_SHEETS ? new (await getSheetsServices()).GoogleSheetsDatabaseService(table)
            : new DatabaseService(table)
        }
        if (table === 'reminders' && prop === 'getActiveReminders') {
          return (await proxy.list('target_date')).filter(r => ['active', 'snoozed'].includes(r.status))
        }
        if (table === 'reminders' && prop === 'snoozeReminder') return proxy.update(args[0], { status: 'snoozed', snoozed_until: args[1], due_at: args[1], is_sent: false, delivered_channels: [], delivery_error: null })
        if (table && prop === 'create') args[0] = { ...(table === 'evidence' ? { relationship: 'unreviewed' } : {}), ...prepareRecord(table, args[0]), workspace_id: user.id }
        if (table && prop === 'update') args[1] = prepareRecord(table, args[1])
        if (table && ['create', 'update'].includes(prop)) {
          const payload = args[prop === 'create' ? 0 : 1]
          if (V2_TABLES.includes(table)) payload.updated_at = new Date().toISOString()
          delete payload.user_id
          if (prop === 'update') { delete payload.id; delete payload.workspace_id }
          for (const [field, relatedTable] of Object.entries(REFERENCE_FIELDS[table] || {})) {
            const ids = Array.isArray(payload[field]) ? payload[field] : payload[field] ? [payload[field]] : []
            for (const id of ids) {
              const related = await createEntityService(relatedTable).getById(id)
              if (!related || related.user_id !== user.id) throw new Error(`The linked ${relatedTable.replaceAll('_', ' ')} record is not in this workspace.`)
            }
          }
        }
        const result = await service[prop](...args)
        if (table && ['create', 'update', 'delete', 'createBulk'].includes(prop)) notifyDataChanged()
        return table ? (Array.isArray(result) ? result.map(r => normalizeRecord(table, r)) : normalizeRecord(table, result)) : result
      }
    }
  })
  return proxy
}

export const createEntityService = (table) => createProviderProxy(new DatabaseService(table))

// ============================================================================
// ORIGINAL SUPABASE IMPLEMENTATION
// Kept intact for rollback capability
// ============================================================================

// Generic database operations
class DatabaseService {
  constructor(tableName) {
    this.tableName = tableName
  }

  async list(orderBy = 'created_at', limit = null, filters = {}) {
    try {
      let query = supabase
        .from(this.tableName)
        .select('*')

      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          query = query.eq(key, value)
        }
      })

      // Apply ordering
      const isDesc = orderBy.startsWith('-')
      const column = isDesc ? orderBy.slice(1) : orderBy
      query = query.order(column, { ascending: !isDesc })
      if (column !== 'id') query = query.order('id', { ascending: true })

      // Apply limit
      if (limit) {
        query = query.limit(limit)
      }

      // Supabase caps an unbounded select at 1,000 rows. Workspace imports and
      // analysis must see every saved source, with a stable order between pages.
      if (!limit) {
        const rows = []
        for (let offset = 0; ; offset += 1000) {
          const { data, error } = await query.range(offset, offset + 999)
          if (error) throw error
          rows.push(...(data || []))
          if (!data || data.length < 1000) return rows
        }
      }

      const { data, error } = await query

      if (error) {
        console.error(`Error fetching ${this.tableName}:`, error)
        throw error
      }

      return data || []
    } catch (error) {
      console.error(`Error in ${this.tableName}.list:`, error)
      throw error
    }
  }

  async create(item) {
    try {
      console.log(`🔄 ${this.tableName}.create: Starting creation...`)
      const { data: user } = await supabase.auth.getUser()
      if (!user?.user?.id) {
        throw new Error('User not authenticated')
      }
      console.log(`✅ ${this.tableName}.create: User authenticated:`, user.user.id)

      const itemWithUser = { ...item, user_id: user.user.id }
      console.log(`📝 ${this.tableName}.create: Item to insert:`, itemWithUser)

      const { data, error } = await supabase
        .from(this.tableName)
        .insert([itemWithUser])
        .select()
        .single()

      if (error) {
        console.error(`❌ ${this.tableName}.create: Database error:`, error)
        throw error
      }

      console.log(`✅ ${this.tableName}.create: Successfully created:`, data)
      return data
    } catch (error) {
      console.error(`💥 ${this.tableName}.create: Caught error:`, error)
      throw error
    }
  }

  async update(id, updates) {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error(`Error updating ${this.tableName}:`, error)
        throw error
      }

      return data
    } catch (error) {
      console.error(`Error in ${this.tableName}.update:`, error)
      throw error
    }
  }

  async delete(id) {
    try {
      const { error } = await supabase
        .from(this.tableName)
        .delete()
        .eq('id', id)

      if (error) {
        console.error(`Error deleting ${this.tableName}:`, error)
        throw error
      }

      return true
    } catch (error) {
      console.error(`Error in ${this.tableName}.delete:`, error)
      throw error
    }
  }

  async getById(id) {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('id', id)
        .maybeSingle()

      if (error) {
        console.error(`Error fetching ${this.tableName} by id:`, error)
        throw error
      }

      return data
    } catch (error) {
      console.error(`Error in ${this.tableName}.getById:`, error)
      throw error
    }
  }
}

// Service instances for each entity - Supabase implementation
const _problemCardsService = new DatabaseService('problem_cards')

// Custom Evidence Service with field mapping
class EvidenceService extends DatabaseService {
  constructor() {
    super('evidence')
  }

  async create(item) {
    try {
      const { data: user } = await supabase.auth.getUser()
      if (!user?.user?.id) {
        throw new Error('User not authenticated')
      }

      // Map frontend fields to database fields
      const mappedItem = {
        user_id: user.user.id,
        problem_card_id: item.problem_id || item.problem_card_id,
        title: item.title || item.note || item.url || `${item.type} evidence`,
        type: item.type,
        source_url: item.url || item.source_url,
        description: item.note || item.description,
        created_at: item.dateISO ? new Date(item.dateISO).toISOString() : new Date().toISOString(),
        confidence_impact: item.confidence_impact || {}
      }

      const { data, error } = await supabase
        .from(this.tableName)
        .insert([mappedItem])
        .select()
        .single()

      if (error) {
        console.error(`Error creating ${this.tableName}:`, error)
        throw error
      }

      return data
    } catch (error) {
      console.error(`Error in ${this.tableName}.create:`, error)
      throw error
    }
  }
}

const _evidenceService = new EvidenceService()
const _roadmapItemsService = new DatabaseService('roadmap_items')
const _issuesService = new DatabaseService('issues')
const _okrsService = new DatabaseService('okrs')
const _timeReflectionsService = new DatabaseService('time_reflections')
const _hypothesesService = new DatabaseService('hypotheses')

// Time Entries Service
class TimeEntriesService extends DatabaseService {
  constructor() {
    super('time_entries')
  }

  async getWeekData(startDate, endDate) {
    try {
      const { data: user } = await supabase.auth.getUser()
      if (!user?.user?.id) {
        console.warn('⚠️ No user authenticated for getWeekData')
        return []
      }

      console.log(`📊 Fetching time entries for user ${user.user.id} between ${startDate} and ${endDate}`)

      const { data: allUserEntries } = await supabase
        .from('time_entries')
        .select('*')
        .eq('user_id', user.user.id)

      console.log(`📊 Total entries for user: ${allUserEntries?.length || 0}`)
      if (allUserEntries && allUserEntries.length > 0) {
        console.log('📊 Sample entry dates:', allUserEntries.slice(0, 3).map(e => e.date))
      }

      const { data, error } = await supabase
        .from('time_entries')
        .select('*')
        .eq('user_id', user.user.id)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true })

      if (error) {
        console.error('❌ Error fetching week data:', error)
        throw error
      }

      console.log(`✅ Found ${data?.length || 0} time entries`)
      return data || []
    } catch (error) {
      console.error('💥 Error fetching week data:', error)
      return []
    }
  }

  async createBulk(entries) {
    try {
      const { data: user } = await supabase.auth.getUser()
      if (!user?.user?.id) {
        throw new Error('User not authenticated')
      }

      console.log(`📥 Creating ${entries.length} time entries for user ${user.user.id}`)
      console.log('📥 Sample entry:', entries[0])
      console.log('📥 Entry dates:', entries.map(e => e.date))

      const entriesWithUserId = entries.map(entry => ({
        ...entry,
        user_id: user.user.id
      }))

      const { data, error } = await supabase
        .from('time_entries')
        .insert(entriesWithUserId)
        .select()

      if (error) {
        console.error('❌ Error creating bulk time entries:', error)
        throw error
      }

      console.log(`✅ Successfully created ${data?.length || 0} time entries`)
      return data
    } catch (error) {
      console.error('💥 Error creating bulk time entries:', error)
      throw error
    }
  }
}

const _timeEntriesService = new TimeEntriesService()

// Problem Cards Service
class ProblemCardsService extends DatabaseService {
  constructor() {
    super('problem_cards')
  }

  async getWithEvidence(problemCardId) {
    try {
      const { data, error } = await supabase
        .from('problem_cards')
        .select(`
          *,
          evidence (*)
        `)
        .eq('id', problemCardId)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching problem card with evidence:', error)
      return null
    }
  }

  async updateConfidenceScore(id, confidenceScore) {
    return this.update(id, { confidence_score: confidenceScore })
  }
}

// Roadmap Items Service
class RoadmapItemsService extends DatabaseService {
  constructor() {
    super('roadmap_items')
  }

  async getByTimeHorizon(horizon) {
    return this.list('priority_score', null, { time_horizon: horizon })
  }

  async linkToProblemCards(roadmapItemId, problemCardIds) {
    return this.update(roadmapItemId, { problem_card_ids: problemCardIds })
  }
}

// Issues Service
class IssuesService extends DatabaseService {
  constructor() {
    super('issues')
  }

  async getByTier(tier) {
    return this.list('-updated_date', null, { tier })
  }

  async bulkUpdateTier(issueIds, tier) {
    try {
      const { data, error } = await supabase
        .from('issues')
        .update({ tier })
        .in('id', issueIds)
        .select()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error bulk updating issue tiers:', error)
      throw error
    }
  }
}

// User Settings Service
class UserSettingsService {
  async getSettings() {
    try {
      const { data: user } = await supabase.auth.getUser()
      if (!user?.user?.id) {
        throw new Error('User not authenticated')
      }

      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.user.id)
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error
      }

      return data || {}
    } catch (error) {
      console.error('Error fetching user settings:', error)
      return {}
    }
  }

  async updateSettings(settings) {
    try {
      const { data: user } = await supabase.auth.getUser()
      if (!user?.user?.id) {
        throw new Error('User not authenticated')
      }

      const { data, error } = await supabase
        .from('user_settings')
        .upsert([{ user_id: user.user.id, ...settings }])
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating user settings:', error)
      throw error
    }
  }

  async updateJiraConfig(jiraConfig) {
    const settings = await this.getSettings()
    return this.updateSettings({ ...settings, jira_config: jiraConfig })
  }

  async updateSlackConfig(slackConfig) {
    const settings = await this.getSettings()
    return this.updateSettings({ ...settings, slack_config: slackConfig })
  }

  async updateNotificationPreferences(preferences) {
    const settings = await this.getSettings()
    return this.updateSettings({ ...settings, notification_preferences: preferences })
  }
}

// Reminders Service
class RemindersService extends DatabaseService {
  constructor() {
    super('reminders')
  }

  async getActiveReminders() {
    try {
      const { data: user } = await supabase.auth.getUser()
      if (!user?.user?.id) {
        throw new Error('User not authenticated')
      }

      const { data, error } = await supabase
        .from('reminders')
        .select('*')
        .eq('user_id', user.user.id)
        .eq('status', 'active')
        .order('target_date', { ascending: true })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching active reminders:', error)
      return []
    }
  }

  async getRemindersForRoadmapItem(roadmapItemId) {
    try {
      const { data, error } = await supabase
        .from('reminders')
        .select('*')
        .eq('roadmap_item_id', roadmapItemId)
        .order('target_date', { ascending: true })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching reminders for roadmap item:', error)
      return []
    }
  }

  async snoozeReminder(reminderId, snoozeUntil) {
    return this.update(reminderId, {
      status: 'snoozed',
      snoozed_until: snoozeUntil
    })
  }

  async markAsSent(reminderId) {
    return this.update(reminderId, { is_sent: true })
  }

  async completeReminder(reminderId) {
    return this.update(reminderId, { status: 'completed' })
  }

  async getDueReminders() {
    try {
      const { data: user } = await supabase.auth.getUser()
      if (!user?.user?.id) {
        throw new Error('User not authenticated')
      }

      const today = new Date().toISOString().split('T')[0]
      const { data, error } = await supabase
        .from('reminders')
        .select('*, roadmap_items(*)')
        .eq('user_id', user.user.id)
        .eq('status', 'active')
        .lte('target_date', today)
        .is('snoozed_until', null)

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching due reminders:', error)
      return []
    }
  }

  async getUpcomingReminders(daysAhead = 30) {
    try {
      const { data: user } = await supabase.auth.getUser()
      if (!user?.user?.id) {
        throw new Error('User not authenticated')
      }

      const today = new Date().toISOString().split('T')[0]
      const futureDate = new Date(Date.now() + daysAhead * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

      const { data, error } = await supabase
        .from('reminders')
        .select('*, roadmap_items(*)')
        .eq('user_id', user.user.id)
        .eq('status', 'active')
        .gte('target_date', today)
        .lte('target_date', futureDate)
        .order('target_date', { ascending: true })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching upcoming reminders:', error)
      return []
    }
  }
}

// Notification History Service
class NotificationHistoryService extends DatabaseService {
  constructor() {
    super('notification_history')
  }

  async logNotification(reminderId, notificationType, status = 'sent', channelDetails = {}, errorMessage = null) {
    try {
      const { data: user } = await supabase.auth.getUser()
      if (!user?.user?.id) {
        throw new Error('User not authenticated')
      }

      const { data, error } = await supabase
        .from('notification_history')
        .insert([{
          user_id: user.user.id,
          reminder_id: reminderId,
          notification_type: notificationType,
          status,
          channel_details: channelDetails,
          error_message: errorMessage
        }])
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error logging notification:', error)
      throw error
    }
  }

  async getNotificationHistory(reminderId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('notification_history')
        .select('*')
        .eq('reminder_id', reminderId)
        .order('sent_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching notification history:', error)
      return []
    }
  }
}

// Instantiate Supabase services
const _problemCards = new ProblemCardsService()
const _roadmapItems = new RoadmapItemsService()
const _issues = new IssuesService()
const _timeEntries = new TimeEntriesService()
const _userSettings = new UserSettingsService()
const _reminders = new RemindersService()
const _notificationHistory = new NotificationHistoryService()

// ============================================================================
// EXPORTED SERVICES (with provider switching)
// ============================================================================

// Export services with provider-aware proxies
export const problemCardsService = createProviderProxy(_problemCardsService, 'sheetsProblemCardsService', 'problemCardsService')
export const evidenceService = createProviderProxy(_evidenceService, 'sheetsEvidenceService', 'evidenceService')
export const roadmapItemsService = createProviderProxy(_roadmapItemsService, 'sheetsRoadmapItemsService', 'roadmapItemsService')
export const issuesService = createProviderProxy(_issuesService, 'sheetsIssuesService', 'issuesService')
export const okrsService = createProviderProxy(_okrsService, 'sheetsOkrsService', 'okrsService')
export const timeReflectionsService = createProviderProxy(_timeReflectionsService, 'sheetsTimeReflectionsService', 'timeReflectionsService')
export const hypothesesService = createProviderProxy(_hypothesesService, 'sheetsHypothesesService', 'hypothesesService')
export const timeEntriesService = createProviderProxy(_timeEntriesService, 'sheetsTimeEntriesService', 'timeEntriesService')

// Specialized services
export const problemCards = createProviderProxy(_problemCards, 'sheetsProblemCards', 'problemCards')
export const roadmapItems = createProviderProxy(_roadmapItems, 'sheetsRoadmapItems', 'roadmapItems')
export const issues = createProviderProxy(_issues, 'sheetsIssues', 'issues')
export const timeEntries = createProviderProxy(_timeEntries, 'sheetsTimeEntries', 'timeEntries')
export const userSettings = createProviderProxy(_userSettings, 'sheetsUserSettings', 'userSettings')
export const reminders = createProviderProxy(_reminders, 'sheetsReminders', 'reminders')
export const notificationHistory = createProviderProxy(_notificationHistory, 'sheetsNotificationHistory', 'notificationHistory')

// Re-export classes for consumers that need them
export {
  EvidenceService,
  TimeEntriesService,
  ProblemCardsService,
  RoadmapItemsService,
  IssuesService,
  UserSettingsService,
  RemindersService,
  NotificationHistoryService
}

// Migration helper to move localStorage data to database
export const migrateLocalStorageToDatabase = async () => {
  try {
    const { data: user } = await supabase.auth.getUser()
    if (!user?.user?.id) {
      console.log('No user authenticated for migration')
      return
    }

    console.log('Starting migration from localStorage to Supabase...')

    // Migrate issues
    const localIssues = JSON.parse(localStorage.getItem(`spcc:user:${user.user.id}:issues`) || '[]')
    if (localIssues.length > 0) {
      console.log(`Migrating ${localIssues.length} issues...`)
      for (const issue of localIssues) {
        // Map localStorage fields to schema fields
        const mappedIssue = {
          external_id: issue.id,
          title: issue.title,
          description: issue.summary || issue.description || '',
          status: issue.status || 'new',
          priority: issue.tier === 'tier1' ? 'critical' : issue.tier === 'tier2' ? 'high' : 'medium',
          severity: issue.tier === 'tier1' ? 'critical' : issue.tier === 'tier2' ? 'high' : 'medium',
          tier: issue.tier === 'tier1' ? 1 : issue.tier === 'tier2' ? 2 : 3,
          source: issue.source || 'manual',
          assignee: issue.owner || null,
          labels: issue.tags || [],
          created_date: issue.created_date,
          updated_date: issue.updated_date,
          external_url: issue.url || null,
          triage_notes: issue.impact ? `Impact: ${issue.impact}` : null
        }
        await issues.create(mappedIssue)
      }
      localStorage.removeItem(`spcc:user:${user.user.id}:issues`)
    }

    // Migrate time reflections
    const localReflections = JSON.parse(localStorage.getItem(`spcc:user:${user.user.id}:time-reflections`) || '[]')
    if (localReflections.length > 0) {
      console.log(`Migrating ${localReflections.length} time reflections...`)
      for (const reflection of localReflections) {
        await timeReflectionsService.create({
          content: reflection.content,
          week_identifier: reflection.week || 'current',
          created_at: reflection.timestamp
        })
      }
      localStorage.removeItem(`spcc:user:${user.user.id}:time-reflections`)
    }

    console.log('Migration completed successfully!')
  } catch (error) {
    console.error('Migration failed:', error)
  }
}

// ============================================================================
// PROVIDER INFORMATION EXPORTS
// ============================================================================

export const getDbProvider = () => DB_PROVIDER
export const isUsingGoogleSheets = () => USE_GOOGLE_SHEETS
export const isUsingLocal = () => USE_LOCAL
export const isUsingFirebase = () => USE_FIREBASE
export const isUsingSupabase = () => !USE_GOOGLE_SHEETS && !USE_LOCAL && !USE_FIREBASE
